import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOverlayContext } from 'seagull';
import { useClientContacts } from '~/hooks/reactQuery/queries/useClientContacts';
import { useClients } from '~/hooks/reactQuery/queries/useClients';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
import { useStore, useStoreActions } from '~/store/store-context';
import { AD_HOC_OPTION_VALUE, classifyContactAgreementState, ContactAgreementState } from '../../GlobalBillModal/GlobalBillModal.utils';
import { findAgreementSummary, splitBillableSummaries, summaryBuyerLabel, toAgreementOptions } from '../utils/billRecipient.utils';
import { useBillRecipientModals } from './useBillRecipientModals';
const AGREEMENTS_ERROR_TOAST_ID = 'bill-recipient-agreements-error';
export const useBillRecipientSelector = (onResolved)=>{
    var _ref, _ref1, _ref2;
    var _lockedSummary_buyer;
    const { t } = useLocaleKeys('components.billRecipientSelector');
    const overlayContext = useOverlayContext();
    const { openCreateClient, openCreateContact } = useBillRecipientModals();
    const { api } = useStoreActions();
    const businessId = useStore(selectors.user.businessId);
    const isVendor = useStore(selectors.user.isVendor);
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [summaries, setSummaries] = useState([]);
    const [agreementsLoading, setAgreementsLoading] = useState(false);
    const [selectedAgreementValue, setSelectedAgreementValue] = useState(null);
    const [selectedContact, setSelectedContact] = useState(null);
    const { clients, isLoading: clientsLoading, error: clientsError, refresh: refreshClients } = useClients({
        refreshOnHookMount: true
    });
    const { agreements, adHocSummary } = useMemo(()=>splitBillableSummaries(summaries), [
        summaries
    ]);
    const agreementState = useMemo(()=>classifyContactAgreementState(toAgreementOptions(agreements), adHocSummary), [
        agreements,
        adHocSummary
    ]);
    const isIwaPath = agreementState === ContactAgreementState.NoSignedAgreement || selectedAgreementValue === AD_HOC_OPTION_VALUE;
    const { contacts, isLoading: contactsLoading, error: contactsError, refresh: refreshContacts } = useClientContacts(isIwaPath ? selectedClientId : null, {
        refreshOnHookMount: true
    });
    const fetchAgreements = useCallback(async (clientId)=>{
        if (businessId == null) {
            return;
        }
        setAgreementsLoading(true);
        try {
            const response = await api.relationship.getSummaries({
                businessId,
                customerId: clientId,
                type: 'agreementAndAdHoc',
                isVendor
            });
            setSummaries(response);
        } catch (unused) {
            setSummaries([]);
            setSelectedAgreementValue(null);
            overlayContext.showErrorToast(t.failedToLoadAgreements(), AGREEMENTS_ERROR_TOAST_ID);
        } finally{
            setAgreementsLoading(false);
        }
    }, [
        businessId,
        isVendor,
        overlayContext
    ]);
    const resetForClient = useCallback((clientId)=>{
        setSelectedClientId(clientId);
        setSummaries([]);
        setSelectedAgreementValue(null);
        setSelectedContact(null);
    }, []);
    const onSelectClient = useCallback((clientId)=>{
        resetForClient(clientId);
        void fetchAgreements(clientId);
    }, [
        fetchAgreements,
        resetForClient
    ]);
    const onCreateClient = useCallback(()=>{
        openCreateClient(async (clientId)=>{
            const { data } = await refreshClients({
                throwOnError: false
            });
            if (data === null || data === void 0 ? void 0 : data.some((client)=>client.clientId() === clientId)) {
                onSelectClient(clientId);
            }
        });
    }, [
        openCreateClient,
        refreshClients,
        onSelectClient
    ]);
    const onCreateContact = useCallback(()=>{
        if (selectedClientId == null) {
            return;
        }
        openCreateContact(async (contactId)=>{
            const { data } = await refreshContacts({
                throwOnError: false
            });
            const created = data === null || data === void 0 ? void 0 : data.find((contact)=>contact.id() === contactId);
            if (created != null) {
                setSelectedContact(created);
            }
        });
    }, [
        openCreateContact,
        refreshContacts,
        selectedClientId
    ]);
    const isSingleAgreement = agreementState === ContactAgreementState.OnlySignedAgreements && agreements.length === 1;
    useEffect(()=>{
        if (isSingleAgreement && selectedAgreementValue == null) {
            setSelectedAgreementValue(agreements[0].id);
        }
    }, [
        isSingleAgreement,
        selectedAgreementValue,
        agreements
    ]);
    const lockedSummary = isIwaPath ? null : findAgreementSummary(agreements, selectedAgreementValue);
    const billTo = useMemo(()=>{
        if (selectedClientId == null) {
            return {
                kind: 'hidden'
            };
        }
        if (isIwaPath) {
            return {
                kind: 'iwa',
                contacts,
                isLoading: contactsLoading,
                selectedContact,
                onSelectContact: setSelectedContact,
                onCreateContact
            };
        }
        if (lockedSummary == null) {
            return {
                kind: 'hidden'
            };
        }
        if (lockedSummary.buyer == null) {
            return {
                kind: 'locked',
                buyerLabel: ''
            };
        }
        return {
            kind: 'locked',
            buyerLabel: summaryBuyerLabel(lockedSummary.buyer)
        };
    }, [
        selectedClientId,
        isIwaPath,
        contacts,
        contactsLoading,
        selectedContact,
        onCreateContact,
        lockedSummary
    ]);
    const lockedRelationshipId = (_ref = lockedSummary === null || lockedSummary === void 0 ? void 0 : lockedSummary.id) !== null && _ref !== void 0 ? _ref : null;
    const lockedBuyerId = (_ref1 = lockedSummary === null || lockedSummary === void 0 ? void 0 : (_lockedSummary_buyer = lockedSummary.buyer) === null || _lockedSummary_buyer === void 0 ? void 0 : _lockedSummary_buyer.id) !== null && _ref1 !== void 0 ? _ref1 : null;
    const selectedContactId = (_ref2 = selectedContact === null || selectedContact === void 0 ? void 0 : selectedContact.id()) !== null && _ref2 !== void 0 ? _ref2 : null;
    const hasMissingBuyer = lockedSummary != null && lockedSummary.buyer == null;
    useEffect(()=>{
        if (selectedClientId == null) {
            onResolved(null);
            return;
        }
        if (isIwaPath) {
            onResolved(selectedContactId == null ? null : {
                clientId: selectedClientId,
                relationshipId: null,
                recipientContactId: selectedContactId
            });
            return;
        }
        if (lockedRelationshipId == null || lockedBuyerId == null) {
            onResolved(null);
            return;
        }
        onResolved({
            clientId: selectedClientId,
            relationshipId: lockedRelationshipId,
            recipientContactId: lockedBuyerId
        });
    }, [
        selectedClientId,
        isIwaPath,
        selectedContactId,
        lockedRelationshipId,
        lockedBuyerId,
        onResolved
    ]);
    const showAgreementField = agreementState === ContactAgreementState.SignedAndAdHoc || agreementState === ContactAgreementState.OnlySignedAgreements && agreements.length > 1;
    const agreementOptions = useMemo(()=>{
        const options = toAgreementOptions(agreements);
        return agreementState === ContactAgreementState.SignedAndAdHoc ? [
            ...options,
            {
                value: AD_HOC_OPTION_VALUE,
                label: t.invoicesWithoutAgreement()
            }
        ] : options;
    }, [
        agreements,
        agreementState
    ]);
    return {
        clients,
        clientsLoading,
        clientsError: clientsError != null,
        contactsError: contactsError != null,
        hasMissingBuyer,
        selectedClientId,
        onSelectClient,
        onCreateClient,
        onRetryClients: ()=>void refreshClients({
                throwOnError: false
            }),
        showAgreementField: selectedClientId != null && showAgreementField,
        agreementOptions,
        selectedAgreementValue,
        onSelectAgreement: setSelectedAgreementValue,
        agreementsLoading,
        billTo,
        showNoAgreementCallout: agreementState === ContactAgreementState.NoSignedAgreement
    };
};
